import { renameKeysFromRESTResponseToSDKResponse } from '@wix/sdk-runtime/rename-all-nested-keys';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { transformRESTVideoV2ToSDKVideoV2 } from '@wix/sdk-runtime/transformations/video-v2';
import { EventDefinition, HttpClient, NonNullablePaths } from '@wix/sdk-types';
import {
  BulkCreateProjectItemsOptions,
  BulkCreateProjectItemsResponse,
  BulkDeleteProjectItemsOptions,
  BulkDeleteProjectItemsResponse,
  BulkUpdateProjectItemsOptions,
  BulkUpdateProjectItemsResponse,
  DeleteProjectItemResponse,
  DuplicateProjectItemsOptions,
  DuplicateProjectItemsResponse,
  GenerateTokenForProjectItemsResponse,
  Item,
  ListProjectItemsOptions,
  ListProjectItemsResponse,
  ProjectItemCreatedEnvelope,
  ProjectItemDeletedEnvelope,
  ProjectItemUpdatedEnvelope,
  UpdateProjectItem,
  bulkCreateProjectItems as universalBulkCreateProjectItems,
  bulkDeleteProjectItems as universalBulkDeleteProjectItems,
  bulkUpdateProjectItems as universalBulkUpdateProjectItems,
  createProjectItem as universalCreateProjectItem,
  deleteProjectItem as universalDeleteProjectItem,
  duplicateProjectItems as universalDuplicateProjectItems,
  generateTokenForProjectItems as universalGenerateTokenForProjectItems,
  getProjectItem as universalGetProjectItem,
  listProjectItems as universalListProjectItems,
  updateProjectItem as universalUpdateProjectItem,
} from './portfolio-project-items-v1-project-item-project-items.universal.js';

export const __metadata = { PACKAGE_NAME: '@wix/portfolio' };

export function createProjectItem(
  httpClient: HttpClient
): CreateProjectItemSignature {
  return (item: Item) =>
    universalCreateProjectItem(
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface CreateProjectItemSignature {
  /**
   * Creates a project item.
   * @param - Project item to create.
   * @returns Newly created project item.
   */
  (item: Item): Promise<
    NonNullablePaths<
      Item,
      `image.focalPoint.x` | `image.focalPoint.y` | `type`,
      4
    >
  >;
}

export function bulkCreateProjectItems(
  httpClient: HttpClient
): BulkCreateProjectItemsSignature {
  return (
    options?: NonNullablePaths<BulkCreateProjectItemsOptions, `items`, 2>
  ) =>
    universalBulkCreateProjectItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkCreateProjectItemsSignature {
  /**
   * Creates multiple project items.
   *
   * To create a single project item, call Create Project Item.
   */
  (
    options?: NonNullablePaths<BulkCreateProjectItemsOptions, `items`, 2>
  ): Promise<
    NonNullablePaths<
      BulkCreateProjectItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function getProjectItem(
  httpClient: HttpClient
): GetProjectItemSignature {
  return (itemId: string) =>
    universalGetProjectItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface GetProjectItemSignature {
  /**
   * Retrieves a project item.
   * @param - Project item ID.
   * @returns Project item.
   */
  (itemId: string): Promise<
    NonNullablePaths<
      Item,
      `image.focalPoint.x` | `image.focalPoint.y` | `type`,
      4
    >
  >;
}

export function listProjectItems(
  httpClient: HttpClient
): ListProjectItemsSignature {
  return (projectId: string, options?: ListProjectItemsOptions) =>
    universalListProjectItems(
      projectId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface ListProjectItemsSignature {
  /**
   * Retrieves a list of all project items in the specified project.
   * @param - Project ID.
   */
  (projectId: string, options?: ListProjectItemsOptions): Promise<
    NonNullablePaths<
      ListProjectItemsResponse,
      | `items`
      | `items.${number}.image.focalPoint.x`
      | `items.${number}.image.focalPoint.y`
      | `items.${number}.type`,
      6
    >
  >;
}

export function updateProjectItem(
  httpClient: HttpClient
): UpdateProjectItemSignature {
  return (_id: string, item: UpdateProjectItem) =>
    universalUpdateProjectItem(
      _id,
      item,
      // @ts-ignore
      { httpClient }
    );
}

interface UpdateProjectItemSignature {
  /**
   * Updates a project item.
   * @param - Project item ID.
   * @returns The updated project item.
   */
  (_id: string, item: UpdateProjectItem): Promise<
    NonNullablePaths<
      Item,
      `image.focalPoint.x` | `image.focalPoint.y` | `type`,
      4
    >
  >;
}

export function bulkUpdateProjectItems(
  httpClient: HttpClient
): BulkUpdateProjectItemsSignature {
  return (
    options?: NonNullablePaths<
      BulkUpdateProjectItemsOptions,
      `items.${number}.item` | `items.${number}.item._id`,
      5
    >
  ) =>
    universalBulkUpdateProjectItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkUpdateProjectItemsSignature {
  /**
   * Updates multiple project items.
   *
   * To update a single project item, call Update Project Item.
   */
  (
    options?: NonNullablePaths<
      BulkUpdateProjectItemsOptions,
      `items.${number}.item` | `items.${number}.item._id`,
      5
    >
  ): Promise<
    NonNullablePaths<
      BulkUpdateProjectItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.item.type`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function deleteProjectItem(
  httpClient: HttpClient
): DeleteProjectItemSignature {
  return (itemId: string) =>
    universalDeleteProjectItem(
      itemId,
      // @ts-ignore
      { httpClient }
    );
}

interface DeleteProjectItemSignature {
  /**
   * Deletes a project item.
   * @param - ID of the project item to delete.
   */
  (itemId: string): Promise<
    NonNullablePaths<DeleteProjectItemResponse, `itemId`, 2>
  >;
}

export function bulkDeleteProjectItems(
  httpClient: HttpClient
): BulkDeleteProjectItemsSignature {
  return (
    options: NonNullablePaths<BulkDeleteProjectItemsOptions, `itemIds`, 2>
  ) =>
    universalBulkDeleteProjectItems(
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface BulkDeleteProjectItemsSignature {
  /**
   * Deletes multiple project items.
   *
   * To delete a single project item, call Delete Project Item.
   */
  (
    options: NonNullablePaths<BulkDeleteProjectItemsOptions, `itemIds`, 2>
  ): Promise<
    NonNullablePaths<
      BulkDeleteProjectItemsResponse,
      | `results`
      | `results.${number}.itemMetadata.originalIndex`
      | `results.${number}.itemMetadata.success`
      | `results.${number}.itemMetadata.error.code`
      | `results.${number}.itemMetadata.error.description`
      | `results.${number}.itemId`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      6
    >
  >;
}

export function duplicateProjectItems(
  httpClient: HttpClient
): DuplicateProjectItemsSignature {
  return (
    originProjectId: string,
    options: NonNullablePaths<
      DuplicateProjectItemsOptions,
      `targetProjectId`,
      2
    >
  ) =>
    universalDuplicateProjectItems(
      originProjectId,
      options,
      // @ts-ignore
      { httpClient }
    );
}

interface DuplicateProjectItemsSignature {
  /**
   * Duplicates project items from one project (the origin) to another project (the target).
   *
   * <blockquote class="important">
   * <strong>Important:</strong>
   *
   * Both the origin and target projects must exist before calling this method.
   * </blockquote>
   * @param - ID of the project containing the items to duplicate.
   */
  (
    originProjectId: string,
    options: NonNullablePaths<
      DuplicateProjectItemsOptions,
      `targetProjectId`,
      2
    >
  ): Promise<
    NonNullablePaths<
      DuplicateProjectItemsResponse,
      | `projectId`
      | `bulkActionMetadata.totalSuccesses`
      | `bulkActionMetadata.totalFailures`
      | `bulkActionMetadata.undetailedFailures`,
      3
    >
  >;
}

export function generateTokenForProjectItems(
  httpClient: HttpClient
): GenerateTokenForProjectItemsSignature {
  return (mediaIds: string[]) =>
    universalGenerateTokenForProjectItems(
      mediaIds,
      // @ts-ignore
      { httpClient }
    );
}

interface GenerateTokenForProjectItemsSignature {
  /**
   * Generate media token for project items
   * @param - Media ids of requested project items
   */
  (mediaIds: string[]): Promise<
    NonNullablePaths<
      GenerateTokenForProjectItemsResponse,
      | `mediaTokens`
      | `mediaTokens.${number}.mediaId`
      | `mediaTokens.${number}.mediaToken`,
      4
    >
  >;
}

export const onProjectItemCreated = EventDefinition(
  'wix.portfolio.project_items.v1.project_item_created',
  true,
  (event: ProjectItemCreatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            { path: 'entity.image.focalPoint.x' },
            { path: 'entity.image.focalPoint.y' },
            { path: 'entity.image.unsharpMasking.amount' },
            { path: 'entity.image.unsharpMasking.radius' },
            { path: 'entity.image.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.video.videoInfo' }],
        },
      ])
    )
)<ProjectItemCreatedEnvelope>();
export const onProjectItemDeleted = EventDefinition(
  'wix.portfolio.project_items.v1.project_item_deleted',
  true,
  (event: ProjectItemDeletedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'undefined.sortOrder' },
            { path: 'undefined.image.focalPoint.x' },
            { path: 'undefined.image.focalPoint.y' },
            { path: 'undefined.image.unsharpMasking.amount' },
            { path: 'undefined.image.unsharpMasking.radius' },
            { path: 'undefined.image.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'undefined.createdDate' },
            { path: 'undefined.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'undefined.image.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'undefined.video.videoInfo' }],
        },
      ])
    )
)<ProjectItemDeletedEnvelope>();
export const onProjectItemUpdated = EventDefinition(
  'wix.portfolio.project_items.v1.project_item_updated',
  true,
  (event: ProjectItemUpdatedEnvelope) =>
    renameKeysFromRESTResponseToSDKResponse(
      transformPaths(event, [
        {
          transformFn: transformRESTFloatToSDKFloat,
          paths: [
            { path: 'entity.sortOrder' },
            { path: 'entity.image.focalPoint.x' },
            { path: 'entity.image.focalPoint.y' },
            { path: 'entity.image.unsharpMasking.amount' },
            { path: 'entity.image.unsharpMasking.radius' },
            { path: 'entity.image.unsharpMasking.threshold' },
          ],
        },
        {
          transformFn: transformRESTTimestampToSDKTimestamp,
          paths: [
            { path: 'entity.createdDate' },
            { path: 'entity.updatedDate' },
            { path: 'metadata.eventTime' },
          ],
        },
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [{ path: 'entity.image.imageInfo' }],
        },
        {
          transformFn: transformRESTVideoV2ToSDKVideoV2,
          paths: [{ path: 'entity.video.videoInfo' }],
        },
      ])
    )
)<ProjectItemUpdatedEnvelope>();

export {
  AccountInfo,
  AccountInfoMetadata,
  ActionEvent,
  App,
  ApplicationError,
  BaseEventMetadata,
  BulkActionMetadata,
  BulkCreateProjectItemResult,
  BulkCreateProjectItemsOptions,
  BulkCreateProjectItemsRequest,
  BulkCreateProjectItemsResponse,
  BulkDeleteProjectItemResult,
  BulkDeleteProjectItemsOptions,
  BulkDeleteProjectItemsRequest,
  BulkDeleteProjectItemsResponse,
  BulkUpdateProjectItemResult,
  BulkUpdateProjectItemsOptions,
  BulkUpdateProjectItemsRequest,
  BulkUpdateProjectItemsResponse,
  CreateProjectGalleryRequest,
  CreateProjectGalleryResponse,
  CreateProjectItemRequest,
  CreateProjectItemResponse,
  CursorPaging,
  Cursors,
  CustomTag,
  DeleteProjectItemRequest,
  DeleteProjectItemResponse,
  DeletedProjectRestored,
  DomainEvent,
  DomainEventBodyOneOf,
  DuplicateProjectItemsOptions,
  DuplicateProjectItemsRequest,
  DuplicateProjectItemsResponse,
  Empty,
  EntityCreatedEvent,
  EntityDeletedEvent,
  EntityUpdatedEvent,
  EventMetadata,
  File,
  GenerateTokenForProjectItemsRequest,
  GenerateTokenForProjectItemsResponse,
  GetProjectItemRequest,
  GetProjectItemResponse,
  IdentificationData,
  IdentificationDataIdOneOf,
  Image,
  ImageType,
  InvalidateCache,
  InvalidateCacheGetByOneOf,
  Item,
  ItemMetadata,
  ItemMetadataOneOf,
  Link,
  ListProjectItemsOptions,
  ListProjectItemsRequest,
  ListProjectItemsResponse,
  MaskedItem,
  MessageEnvelope,
  Page,
  Pages,
  Paging,
  PagingMetadataV2,
  Point,
  ProjectItemCreatedEnvelope,
  ProjectItemDeletedEnvelope,
  ProjectItemMediaToken,
  ProjectItemUpdatedEnvelope,
  QueryProjectItemsRequest,
  QueryProjectItemsResponse,
  QueryV2,
  QueryV2PagingMethodOneOf,
  RestoreInfo,
  SortOrder,
  Sorting,
  Tags,
  Type,
  URI,
  URIs,
  UnsharpMasking,
  UpdateProjectItem,
  UpdateProjectItemRequest,
  UpdateProjectItemResponse,
  Video,
  VideoResolution,
  WebhookIdentityType,
} from './portfolio-project-items-v1-project-item-project-items.universal.js';
